import { Component, Input, OnInit, OnChanges, SimpleChanges, ElementRef, ViewChild, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';

import { gsap } from 'gsap';

import { Quota } from '@apps/ostk/domain/models';
import { QuotaSettings } from './quota-settings';

@Component({
    selector: 'aa-quota',
    templateUrl: './quota.component.html',
    styleUrls: [
        './quota.component.scss'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class QuotaComponent implements OnInit, AfterViewInit, OnChanges
{
    @ViewChild('quota', { static: true }) private quotaContainer: ElementRef;
    @Input() settings: QuotaSettings;
    @Input() quota: Quota;
    public mySettings: QuotaSettings = null;
    public svgWidth: number;
    public svgHeight: number;
    public radius: number;
    public gaugePos: string;
    public leftLegendPos: string;
    public rightLegendPos: string;
    public viewBox: string;
    public leftLegend: string;
    public rightLegend: string;
    public neededQuotaColor: string;
    public outerWidth: number;
    private ratio: number = 100;
    private consoPercent: number;
    private neededPercent: number;
    private consoGauge: SVGElement;
    private neededGauge: SVGElement;
    private duration: number = 0.5;

    constructor (private elementRef: ElementRef)
    {
    }

    ngOnInit ()
    {
        this.initSvg();
    }

    ngAfterViewInit ()
    {
        this.initSvg();
    }

    ngOnChanges (changes: SimpleChanges): void
    {
        if (changes.needed)
        {
            this.quota.needed = parseInt(changes.needed.currentValue, 10);
        }

        this.setColors();
    }

    private initSvg ()
    {
        const element = this.quotaContainer.nativeElement;

        this.mySettings = QuotaSettings.mapperFromObject(this.settings);

        this.leftLegend = this.mySettings.legend;

        const doubleHeight: number = 2 * this.mySettings.height;
        const ratio: number = this.ratio / 100;
        const ratio1: number = (100 - this.ratio) / 200;

        this.consoGauge = this.elementRef.nativeElement.querySelector('rect#consoGauge');
        this.neededGauge = this.elementRef.nativeElement.querySelector('rect#neededGauge');

        const { clientWidth : width } = element;
        this.svgWidth = this.mySettings.width ? this.mySettings.width : width;

        this.outerWidth = ratio * this.svgWidth;

        this.viewBox = `0 0 ${this.svgWidth} 10`;

        this.svgHeight = 4 * this.mySettings.height;
        this.radius = this.mySettings.height / 3;

        this.gaugePos = `translate(${ratio1 * this.svgWidth}, ${doubleHeight})`;

        this.leftLegendPos = 'translate(0, 0)';
        this.rightLegendPos = `translate(${this.outerWidth}, 0)`;

        this.setColors();
    }

    private setColors ()
    {
        if (this.mySettings === null)
        {
            return;
        }

        if ((this.quota.used + this.quota.needed) > this.quota.max)
        {
            this.neededQuotaColor = this.mySettings.colors.notEnough;
        }
        else
        {
            this.neededQuotaColor = this.mySettings.colors.needed;
        }

        this.moveGauge();
    }

    private moveGauge ()
    {
        this.consoPercent = 100 * this.quota.used / this.quota.max;
        const consoWidth: number = this.consoPercent / 100 * this.outerWidth;

        this.neededPercent = 100 * (Math.min(this.quota.max, this.quota.used + this.quota.needed)) / this.quota.max;
        const neededWidth: number = this.neededPercent / 100 * this.outerWidth;

        gsap.timeline()
            .to(
                this.consoGauge,
                {
                    duration: this.duration,
                    width: consoWidth,
                    ease: 'sine'
                });

        gsap.timeline()
            .to(
                this.neededGauge,
                {
                    duration: this.duration,
                    width: neededWidth,
                    ease: 'sine'
                });
        this.formatRightLegend();
    }

    private formatRightLegend = () =>
    {
        if (this.quota.needed === 0)
        {
            this.rightLegend = '';

            return;
        }

        this.rightLegend = `${this.quota.needed} ${this.quota.needed > 1 ? this.mySettings.unit.plural : this.mySettings.unit.singular}`;
    };
}
